import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import { id } from 'date-fns/locale';
import { Search, RotateCcw } from 'lucide-react';

// Label & warna per jenis aksi (palet brand: biru/orange + merah utk hapus/tolak, hijau utk pembayaran)
const ACTION_META = {
  // Workflow artikel
  article_requested:    { label: 'Pengajuan Artikel',       color: 'var(--in-blue)',   group: 'Artikel' },
  article_created:      { label: 'Artikel Dibuat Editor',   color: 'var(--in-blue)',   group: 'Artikel' },
  assigned:             { label: 'Ditugaskan ke Editor',    color: 'var(--in-blue)',   group: 'Artikel' },
  reassigned:           { label: 'Ditugaskan Ulang',        color: 'var(--in-orange)', group: 'Artikel' },
  submitted_producer:   { label: 'Dikirim ke Producer',     color: 'var(--in-blue)',   group: 'Artikel' },
  producer_approved:    { label: 'Disetujui Producer',      color: 'var(--in-blue)',   group: 'Artikel' },
  producer_revised:     { label: 'Revisi oleh Producer',    color: 'var(--in-orange)', group: 'Artikel' },
  reader_approved:      { label: 'Disetujui Pengaju',       color: 'var(--in-blue)',   group: 'Artikel' },
  reader_revised:       { label: 'Revisi oleh Pengaju',     color: 'var(--in-orange)', group: 'Artikel' },
  published:            { label: 'Dipublikasikan',          color: 'var(--in-orange)', group: 'Artikel' },
  category_changed:     { label: 'Kategori Diubah',         color: 'var(--in-blue)',   group: 'Artikel' },
  published_date_changed: { label: 'Tanggal Terbit Diubah', color: 'var(--in-blue)',   group: 'Artikel' },
  live_edit_requested:  { label: 'Permintaan Edit Terbit',  color: 'var(--in-orange)', group: 'Artikel' },
  live_edit_submitted:  { label: 'Hasil Edit Dikirim',      color: 'var(--in-blue)',   group: 'Artikel' },
  live_edit_applied:    { label: 'Perubahan Diterapkan',    color: 'var(--in-orange)', group: 'Artikel' },
  deleted:              { label: 'Artikel Dihapus',         color: '#DC2626',          group: 'Artikel' },
  visibility_changed:   { label: 'Visibilitas Diubah',      color: 'var(--in-orange)', group: 'Artikel' },
  // Pembayaran
  payment_proof_uploaded: { label: 'Bukti Transfer Diunggah', color: 'var(--in-blue)', group: 'Pembayaran' },
  payment_verified:     { label: 'Pembayaran Diverifikasi', color: '#059669',          group: 'Pembayaran' },
  payment_paid:         { label: 'Pembayaran Lunas',        color: '#059669',          group: 'Pembayaran' },
  payment_rejected:     { label: 'Bukti Transfer Ditolak',  color: '#DC2626',          group: 'Pembayaran' },
  payment_cancelled:    { label: 'Pengajuan Dibatalkan',    color: '#DC2626',          group: 'Pembayaran' },
  payment_expired:      { label: 'Kedaluwarsa (1×24 jam)',  color: 'var(--in-text-muted)', group: 'Pembayaran' },
};

function meta(action) {
  return ACTION_META[action] || { label: action, color: 'var(--in-text-muted)', group: 'Lainnya' };
}

const ROLE_OPTIONS = [
  { value: 'all', label: 'Semua Peran' },
  { value: 'reader', label: 'Reader' },
  { value: 'editor', label: 'Editor' },
  { value: 'producer', label: 'Producer' },
  { value: 'admin', label: 'Admin' },
  { value: 'system', label: 'Sistem' },
];

// Kelompokkan opsi aksi berdasarkan group
const ACTION_GROUPS = Object.entries(ACTION_META).reduce((acc, [key, m]) => {
  (acc[m.group] = acc[m.group] || []).push({ key, label: m.label });
  return acc;
}, {});

export default function AdminActivity() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const fetchLogs = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '300' });
    if (actionFilter !== 'all') params.set('action', actionFilter);
    if (roleFilter !== 'all') params.set('role', roleFilter);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (search.trim()) params.set('q', search.trim());
    API.get(`/articles/admin/activity?${params.toString()}`)
      .then(r => { setLogs(r.data.logs || []); setTotal(r.data.total || 0); setLoading(false); })
      .catch(() => { toast.error('Gagal memuat log aktivitas'); setLoading(false); });
  }, [actionFilter, roleFilter, from, to, search]);

  // Debounce untuk pencarian teks; filter lain langsung
  useEffect(() => {
    const t = setTimeout(fetchLogs, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchLogs, search]);

  const resetFilters = () => {
    setSearch(''); setActionFilter('all'); setRoleFilter('all'); setFrom(''); setTo('');
  };

  const hasFilter = actionFilter !== 'all' || roleFilter !== 'all' || from || to || search;

  return (
    <DashboardLayout title="Log Aktivitas" subtitle="Riwayat aktivitas pengajuan, pembayaran, dan pengelolaan artikel">
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 8, background: 'var(--in-bg-3)', border: '1px solid var(--ki-border)', borderRadius: 8, padding: '9px 12px' }}>
          <Search size={14} style={{ color: 'var(--ki-text-faint)', flexShrink: 0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari judul, nama, atau keterangan..." style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--ki-text)', fontSize: 13, width: '100%' }} />
        </div>
        <select value={actionFilter} onChange={e => setActionFilter(e.target.value)} className="form-select" style={{ width: 'auto', padding: '9px 14px', fontSize: 13 }}>
          <option value="all">Semua Aktivitas</option>
          {Object.entries(ACTION_GROUPS).map(([group, items]) => (
            <optgroup key={group} label={group}>
              {items.map(it => <option key={it.key} value={it.key}>{it.label}</option>)}
            </optgroup>
          ))}
        </select>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="form-select" style={{ width: 'auto', padding: '9px 14px', fontSize: 13 }}>
          {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ fontSize: 12, color: 'var(--ki-text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
          Dari
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="form-input" style={{ width: 'auto', padding: '7px 10px', fontSize: 13 }} />
        </label>
        <label style={{ fontSize: 12, color: 'var(--ki-text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
          Sampai
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="form-input" style={{ width: 'auto', padding: '7px 10px', fontSize: 13 }} />
        </label>
        {hasFilter && (
          <button onClick={resetFilters} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', gap: 6 }}>
            <RotateCcw size={13} /> Reset Filter
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ki-text-faint)' }}>
          {loading ? 'Memuat...' : `${total} aktivitas`}
        </span>
      </div>

      {loading ? (
        <div className="ki-card">{[...Array(6)].map((_, i) => <div key={i} className="shimmer" style={{ height: 48, borderRadius: 8, marginBottom: 10 }} />)}</div>
      ) : logs.length === 0 ? (
        <div className="ki-card" style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--ki-text-muted)', fontSize: 14 }}>
          {hasFilter ? 'Tidak ada aktivitas yang cocok dengan filter.' : 'Belum ada aktivitas tercatat.'}
        </div>
      ) : (
        <div className="ki-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead><tr><th>Aktivitas</th><th>Artikel</th><th>Oleh</th><th>Keterangan</th><th>Waktu</th></tr></thead>
            <tbody>
              {logs.map(l => {
                const m = meta(l.action);
                return (
                  <tr key={l._id}>
                    <td>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'rgba(0,21,64,0.06)', color: m.color, whiteSpace: 'nowrap' }}>{m.label}</span>
                    </td>
                    <td style={{ fontSize: 13, fontWeight: 600, maxWidth: 280 }}>{l.articleTitle || <span style={{ color: 'var(--ki-text-faint)', fontStyle: 'italic' }}>—</span>}</td>
                    <td style={{ fontSize: 13, color: 'var(--ki-text-muted)' }}>
                      {l.actorName || '—'}
                      {l.actorRole && <span style={{ fontSize: 11, color: 'var(--ki-text-faint)', textTransform: 'capitalize', marginLeft: 6 }}>({l.actorRole})</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ki-text-faint)', maxWidth: 220 }}>{l.detail || '-'}</td>
                    <td style={{ fontSize: 12, color: 'var(--ki-text-faint)', whiteSpace: 'nowrap' }} title={format(new Date(l.createdAt), 'd MMM yyyy, HH.mm', { locale: id })}>
                      {formatDistanceToNow(new Date(l.createdAt), { addSuffix: true, locale: id })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
